import React, { ChangeEvent, useEffect } from 'react'
import {
  Button,
  Card,
  Icon,
  Switch,
  Text,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { CurrencyInputWithSymbol, Currency } from '@vend/business-components'
import { useAppSelector } from '../../../state/hooks'
import {
  selectCustomer,
  selectSaleFinals,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useRegisterManager } from '../../../ng-compat/hooks'
import { getMinimumLsPayAmount } from '../../../util/lspay-utils'

const TOGGLE_CONTEXT = 'Quote payment request toggle label'
const AMOUNT_CONTEXT = 'Quote payment request amount section'
const INFO_CONTEXT = 'Quote payment request info message'
const NOTE_CONTEXT = 'Quote payment request note input'

export interface QuotePaymentRequestState {
  isToggleEnabled: boolean
  amount: string
  note: string
  email: string
}

interface QuotePaymentRequestCardProps {
  state: QuotePaymentRequestState
  onChange: (
    stateOrUpdater:
      | QuotePaymentRequestState
      | ((prev: QuotePaymentRequestState) => QuotePaymentRequestState)
  ) => void
}

export const QuotePaymentRequestCard: React.FC<QuotePaymentRequestCardProps> = ({
  state,
  onChange,
}) => {
  const t = useT()
  const customer = useAppSelector(selectCustomer)
  const saleFinals = useAppSelector(selectSaleFinals)
  const saleTotal = Number(saleFinals.inclusivePrice || 0)
  const customerEmail = customer?.email || ''
  const currentOutlet = useRegisterManager().getActiveOutlet()
  const { minimumAmount } = getMinimumLsPayAmount(currentOutlet)

  useEffect(() => {
    if (customerEmail && !state.email) {
      onChange(prev => ({ ...prev, email: customerEmail }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerEmail])

  useEffect(() => {
    if (state.isToggleEnabled && !state.amount) {
      onChange(prev => ({ ...prev, amount: saleTotal.toFixed(2) }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isToggleEnabled])

  const handleToggle = (e: ChangeEvent<HTMLInputElement>) => {
    const isToggleEnabled = e.target.checked
    onChange({
      ...state,
      isToggleEnabled,
      amount: isToggleEnabled ? saleTotal.toFixed(2) : '',
    })
  }

  const handleAmountChange = (value: number) => {
    onChange({ ...state, amount: String(value) })
  }

  const handleNoteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ ...state, note: e.target.value })
  }

  const requestedAmount = Number(state.amount) || 0
  const remainingBalance = Math.max(saleTotal - requestedAmount, 0)

  return (
    <Card
      className="quote-payment-request-card"
      data-testid="quote-payment-request-card"
    >
      <label className="quote-payment-request-card__toggle">
        <Switch
          small={true}
          checked={state.isToggleEnabled}
          onChange={handleToggle}
          data-testid="quote-payment-request-toggle"
        />
        <div className="quote-payment-request-card__toggle-label">
          <Text>
            <T _str="Request payment" _context={TOGGLE_CONTEXT} />
          </Text>
          {customerEmail && (
            <Text type="supplementary" color="supplementary">
              {customerEmail}
            </Text>
          )}
        </div>
        <Button
          link
          className="quote-payment-request-card__settings-btn"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault()
          }}
          data-testid="quote-payment-request-settings"
        >
          <Icon icon="fa-cog" />
        </Button>
      </label>

      {state.isToggleEnabled && (
        <div
          className="quote-payment-request-card__body"
          data-testid="quote-payment-request-body"
        >
          <div className="quote-payment-request-card__amount-section">
            <Text className="vd-mb2">
              <strong>
                <T _str="Request amount" _context={AMOUNT_CONTEXT} />
              </strong>
            </Text>
            <CurrencyInputWithSymbol
              value={state.amount}
              onChange={handleAmountChange}
              max={saleTotal}
              min={minimumAmount}
              placeholder={saleTotal.toFixed(2)}
              onError={() => {}}
            />
            <Text type="supplementary" color="supplementary" className="vd-mt2">
              <T
                _str="Remaining balance on sale after payment: {amount}"
                _context={AMOUNT_CONTEXT}
                amount={<Currency value={remainingBalance} />}
              />
            </Text>
          </div>

          <div className="quote-payment-request-card__info">
            <Icon
              icon="fa-info-circle"
              className="quote-payment-request-card__info-icon"
            />
            <Text type="supplementary">
              <T
                _str="Once paid, quote is converted to unfulfilled pick up sale."
                _context={INFO_CONTEXT}
              />
            </Text>
          </div>

          <textarea
            className="quote-payment-request-card__note"
            data-testid="quote-payment-request-note"
            placeholder={t('Add note to payment request...', {
              _context: NOTE_CONTEXT,
            })}
            value={state.note}
            onChange={handleNoteChange}
            rows={3}
          />
        </div>
      )}
    </Card>
  )
}
